import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, CircularProgress } from "@material-ui/core";

import ArrowBackRoundedIcon from "@material-ui/icons/ArrowBackRounded";
import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ErrorRoundedIcon from "@material-ui/icons/ErrorRounded";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import ConfirmationNumberRoundedIcon from "@material-ui/icons/ConfirmationNumberRounded";
import GroupRoundedIcon from "@material-ui/icons/GroupRounded";
import QueueRoundedIcon from "@material-ui/icons/QueueRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
// MUI v4 has no SmartToy; alias EmojiObjects (lightbulb-idea) as the
// AI-worker affordance, matching the ContactDrawer convention.
import SmartToyRoundedIcon from "@material-ui/icons/EmojiObjects";
import HistoryRoundedIcon from "@material-ui/icons/HistoryRounded";
import SettingsRoundedIcon from "@material-ui/icons/SettingsRounded";

import { toast } from "react-toastify";
import ExitToAppRoundedIcon from "@material-ui/icons/ExitToAppRounded";
import { impersonateCompany } from "../../helpers/impersonate";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

/**
 * TenantHealthPanel — per-tenant operational drill-down.
 *
 * Privacy contract enforced server-side: this view shows only
 * aggregated counts + configuration metadata. No customer PII,
 * no message bodies, no contact/phone data, no billing card details.
 * See GetTenantHealthService.ts for the full contract.
 */

// === CRM design tokens — verbatim from canonical reference ===
// (Tags/Campaigns/Appointments/Contacts share these constants.)
const SP  = { xxs:4, xs:8, sm:12, md:16, lg:20, xl:24, xxl:32, hero:48 };
const TT  = { min:44, primary:48, hero:56 };
const RAD = { pill:999, sm:8, md:12, lg:16, xl:20 };
const COL = {
  brandGreen:'#25d366', brandGreenDeep:'#128c7e',
  brandGreenSoft:'rgba(37,211,102,0.08)',
  textPrimary:'#0B0D0C', textSecondary:'#52525b',
  borderSoft:'#eaeaea', cardBg:'#ffffff',
  destructive:'#ef4444', warning:'#f59e0b',
};
const TYPE = {
  caption:    { fontSize:12, fontWeight:400, lineHeight:1.4,  letterSpacing:0.2 },
  body:       { fontSize:14, fontWeight:400, lineHeight:1.5,  letterSpacing:0   },
  bodyStrong: { fontSize:15, fontWeight:500, lineHeight:1.45, letterSpacing:0   },
  subheading: { fontSize:17, fontWeight:500, lineHeight:1.4,  letterSpacing:0   },
  heading:    { fontSize:20, fontWeight:600, lineHeight:1.3,  letterSpacing:-0.2 },
  display:    { fontSize:24, fontWeight:700, lineHeight:1.25, letterSpacing:-0.3 },
};

const useStyles = makeStyles((theme) => {
  const isDark     = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const accentTint = isDark ? "rgba(46,227,122,0.04)" : "rgba(18,140,126,0.04)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const fgFaint    = isDark ? "rgba(255,255,255,0.38)" : "#a3a3a3";
  const surface    = isDark ? "#13181a" : "#ffffff";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.025)" : "#fafafa";

  const cardShadow = isDark
    ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.32)"
    : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)";

  return {
    root: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: SP.lg,
      fontFamily: "Roboto, sans-serif",
      [theme.breakpoints.down("xs")]: {
        gap: SP.md,
      },
    },

    // === Breadcrumb ===
    crumb: {
      display: "inline-flex",
      alignItems: "center",
      gap: SP.xs,
      cursor: "pointer",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: fgMuted,
      transition: "color 160ms ease",
      minHeight: TT.min,
      paddingInline: 2,
      whiteSpace: "nowrap",
      "&:hover, &:focus-visible": { color: accent, outline: "none" },
    },
    crumbIcon: {
      fontSize: 16,
    },

    // === Hero (tenant name + status pill + accent meta) ===
    hero: {
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: SP.md,
      padding: "22px 24px",
      borderRadius: RAD.lg,
      background: surface,
      border: `1px solid ${hair}`,
      boxShadow: cardShadow,
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        width: 4,
        background: `linear-gradient(180deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
      },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense hero card: 12px padding, hairline border, near-flat shadow, 8px radius
        padding: "12px 14px",
        gap: 10,
        borderRadius: 8,
        border: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.32)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        "&::before": {
          // Slim status dot strip (6px) for at-a-glance health on mobile
          width: 3,
        },
      },
    },
    heroLeft: {
      display: "flex",
      alignItems: "center",
      gap: SP.md,
      minWidth: 0,
      flex: 1,
    },
    heroAvatar: {
      width: 52,
      height: 52,
      borderRadius: 14,
      display: "grid",
      placeItems: "center",
      background: `linear-gradient(135deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
      color: "#fff",
      fontWeight: 700,
      fontSize: 20,
      letterSpacing: -0.4,
      flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18), 0 4px 12px rgba(18,140,126,0.22)",
    },
    heroStack: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
    },
    accentMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    heroTitle: {
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: -0.2,
      color: fg,
      lineHeight: 1.2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense hero token capped at 16/20 weight 600, wrap not truncate
        fontSize: 16,
        lineHeight: "20px",
        fontWeight: 600,
        color: fg,
        letterSpacing: "-0.01em",
        whiteSpace: "normal",
        display: "block",
        WebkitLineClamp: "unset",
        WebkitBoxOrient: "unset",
        overflow: "visible",
        textOverflow: "clip",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      },
    },
    heroSub: {
      marginTop: 2,
      fontSize: 13,
      color: fgMuted,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
      "& svg": { fontSize: 14, color: fgMuted },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta row: 11/14 weight 400 muted, dot separator
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: fgMuted,
        gap: 4,
        "& svg": { fontSize: 12, color: fgFaint },
      },
    },
    heroSubDot: {
      opacity: 0.4,
    },

    // === Status pill (rounded by status) ===
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 11px",
      borderRadius: RAD.pill,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      border: `1px solid transparent`,
      whiteSpace: "nowrap",
      flexShrink: 0,
      minWidth: "fit-content",
      "& svg": { fontSize: 14, flexShrink: 0 },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense status pill: 2px 6px, radius 4, 10/13 weight 500
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 10,
        lineHeight: "13px",
        fontWeight: 500,
        letterSpacing: "0.04em",
        gap: 4,
        "& svg": { fontSize: 11 },
      },
    },
    statusHealthy: {
      background: isDark ? "rgba(46,227,122,0.14)" : "rgba(37,211,102,0.12)",
      color: isDark ? "#7df0a8" : "#0e6c61",
      borderColor: isDark ? "rgba(46,227,122,0.32)" : "rgba(18,140,126,0.24)",
    },
    statusWatch: {
      background: isDark ? "rgba(251,191,36,0.16)" : "rgba(251,191,36,0.18)",
      color: isDark ? "#fbbf24" : "#b45309",
      borderColor: isDark ? "rgba(251,191,36,0.34)" : "rgba(180,83,9,0.28)",
    },
    statusCritical: {
      background: isDark ? "rgba(252,165,165,0.14)" : "rgba(239,68,68,0.10)",
      color: isDark ? "#fca5a5" : "#b91c1c",
      borderColor: isDark ? "rgba(252,165,165,0.34)" : "rgba(185,28,28,0.24)",
    },

    reasonChip: {
      display: "inline-block",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      padding: "3px 9px",
      borderRadius: RAD.sm,
      background: accentSoft,
      color: accent,
      border: `1px solid ${hair}`,
      marginInlineEnd: 6,
      marginBottom: 4,
      whiteSpace: "nowrap",
    },

    // === Action buttons (hero CTAs) ===
    actions: {
      display: "flex",
      gap: SP.xs,
      flexWrap: "wrap",
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        gap: SP.xs,
        "& > button": { flex: 1, minWidth: "calc(50% - 4px)" },
      },
    },
    btn: {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "10px 16px",
      minHeight: TT.min,
      fontFamily: "Roboto, sans-serif",
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: 0.1,
      borderRadius: RAD.md,
      border: `1px solid ${hair}`,
      background: surface,
      color: fg,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "background 0.14s ease, color 0.14s ease, border-color 0.14s ease, transform 0.18s ease",
      "& svg": { fontSize: 16, flexShrink: 0 },
      "&:hover": {
        background: accentSoft,
        color: accent,
        borderColor: isDark ? "rgba(46,227,122,0.28)" : "rgba(18,140,126,0.22)",
      },
      "&:active": { transform: "scale(0.985)" },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.primary,
        fontSize: 13.5,
        padding: "8px 12px",
      },
    },
    btnPrimary: {
      background: `linear-gradient(135deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
      color: "#fff",
      border: "none",
      boxShadow: "0 4px 12px rgba(18,140,126,0.22)",
      "&:hover": {
        opacity: 0.94,
        background: `linear-gradient(135deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
        color: "#fff",
      },
    },
    btnDanger: {
      borderColor: isDark ? "rgba(252,165,165,0.32)" : "rgba(220,38,38,0.22)",
      color: isDark ? "#fca5a5" : "#b91c1c",
      "&:hover": {
        background: isDark ? "rgba(252,165,165,0.06)" : "rgba(220,38,38,0.06)",
        color: isDark ? "#fca5a5" : "#b91c1c",
        borderColor: isDark ? "rgba(252,165,165,0.5)" : "rgba(220,38,38,0.34)",
      },
    },

    // === Section header (accent meta + title) ===
    sectionHead: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: SP.sm,
      marginBottom: SP.xs,
    },
    sectionLabels: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
    },
    sectionTitle: {
      fontSize: 17,
      fontWeight: 500,
      letterSpacing: 0,
      color: fg,
      lineHeight: 1.3,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      "& svg": { fontSize: 18, color: accent },
    },
    sectionSub: {
      fontSize: 12,
      color: fgMuted,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flexShrink: 0,
      maxWidth: "50%",
    },

    // === KPI grid — 4-up desktop, 2-up xs (cardStackMode v3 cards) ===
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: SP.sm,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: SP.sm,
      },
    },
    kpiCard: {
      position: "relative",
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      padding: "16px 16px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      overflow: "hidden",
      minWidth: 0,
      boxShadow: cardShadow,
      transition: "transform 0.18s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.18s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: accent,
        opacity: 0.85,
      },
      "&:hover": {
        boxShadow: isDark
          ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px rgba(0,0,0,0.38)"
          : "0 1px 0 rgba(15,22,20,0.02), 0 12px 32px rgba(15,22,20,0.06)",
      },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense card: 12px padding, hairline border, 8px radius, near-flat shadow
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.32)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        "&::before": {
          // 6px status accent strip for at-a-glance health
          width: 3,
        },
        "&:hover": {
          boxShadow: isDark
            ? "0 1px 2px rgba(0,0,0,0.4)"
            : "0 1px 2px rgba(10,10,10,0.04)",
        },
      },
    },
    kpiLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
      "& svg": { fontSize: 14, color: accent, flexShrink: 0 },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV label: 12/14 weight 500 muted, sentence-case (no uppercase)
        fontSize: 12,
        lineHeight: "14px",
        fontWeight: 500,
        color: fgMuted,
        letterSpacing: "0.01em",
        textTransform: "none",
        "& svg": { fontSize: 12, color: fgFaint },
      },
    },
    kpiValue: {
      fontSize: 26,
      fontWeight: 600,
      color: fg,
      lineHeight: 1.05,
      letterSpacing: -0.4,
      fontFeatureSettings: '"tnum" 1',
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense hero token capped at 16/20 weight 600
        fontSize: 16,
        lineHeight: "20px",
        fontWeight: 600,
        color: fg,
        letterSpacing: "-0.01em",
      },
    },
    kpiSub: {
      fontSize: 11.5,
      color: fgMuted,
      fontFeatureSettings: '"tnum" 1',
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta token: 11/14 weight 400 muted
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: fgMuted,
      },
    },

    // === Two-column row (spark + config) ===
    twoCol: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: SP.md,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },

    // === Panel (section card) ===
    panel: {
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      padding: SP.lg,
      display: "flex",
      flexDirection: "column",
      gap: SP.sm,
      boxShadow: cardShadow,
      [theme.breakpoints.down("xs")]: {
        // Linear-dense panel card: 12px padding, 8px radius, hairline border, near-flat shadow
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.32)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        gap: 10,
      },
    },

    // === Sparkline (mini-chart with accent stripe) ===
    sparkFrame: {
      position: "relative",
      paddingTop: SP.sm,
      paddingInline: 2,
      paddingBottom: 0,
      borderTop: `1px solid ${hair}`,
    },
    sparkRow: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      alignItems: "end",
      gap: SP.xs,
      minHeight: 110,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 4,
        minHeight: 96,
        rowGap: 8,
        alignItems: "end",
      },
    },
    sparkCol: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      height: "100%",
      minHeight: 80,
    },
    sparkValue: {
      fontSize: 10.5,
      fontWeight: 700,
      color: fg,
      fontFeatureSettings: '"tnum" 1',
      letterSpacing: 0.1,
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        fontSize: 10.5,
      },
    },
    sparkBarWrap: {
      width: "100%",
      flex: 1,
      display: "flex",
      alignItems: "flex-end",
      minHeight: 40,
    },
    sparkBar: {
      width: "100%",
      borderRadius: 4,
      background: `linear-gradient(180deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
      minHeight: 4,
      boxShadow: isDark
        ? "inset 0 1px 0 rgba(255,255,255,0.12)"
        : "inset 0 1px 0 rgba(255,255,255,0.28), 0 1px 2px rgba(18,140,126,0.18)",
      transition: "height 320ms cubic-bezier(.2,.8,.2,1)",
    },
    sparkLabel: {
      fontSize: 10,
      color: fgMuted,
      textTransform: "uppercase",
      letterSpacing: "0.18em",
      fontWeight: 700,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },

    // === Config rows / metric list (label-value pairs) ===
    metricList: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      borderTop: `1px solid ${hair}`,
    },
    configRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: SP.sm,
      padding: "12px 12px",
      borderBottom: `1px solid ${hair}`,
      transition: "background 0.14s ease",
      "&:last-child": { borderBottom: "none" },
      "&:nth-child(even)": {
        background: accentTint,
      },
      "&:hover": {
        background: accentSoft,
      },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV row: stacked, tight 3px rowSpacing per spec
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 3,
        padding: "8px 0",
        "&:nth-child(even)": { background: "transparent" },
        "&:hover": { background: "transparent" },
      },
    },
    configLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
      flex: "1 1 auto",
      "& svg": { fontSize: 14, color: accent, flexShrink: 0 },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV label: 12/14 weight 500 muted, sentence-case (no uppercase)
        fontSize: 12,
        lineHeight: "14px",
        fontWeight: 500,
        color: fgMuted,
        letterSpacing: "0.01em",
        textTransform: "none",
        "& svg": { fontSize: 12, color: fgFaint },
      },
    },
    configValue: {
      fontSize: 14,
      fontWeight: 500,
      color: fg,
      fontFeatureSettings: '"tnum" 1',
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flexShrink: 0,
      maxWidth: "60%",
      textAlign: "end",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense KV value: 12/16 weight 400, wrap not truncate
        fontSize: 12,
        lineHeight: "16px",
        fontWeight: 400,
        color: fg,
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "100%",
        textAlign: "start",
      },
    },
    onPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: RAD.pill,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      border: `1px solid transparent`,
      whiteSpace: "nowrap",
      flexShrink: 0,
      minWidth: 44,
      justifyContent: "center",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense status pill: 2px 6px, radius 4, 10/13 weight 500
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 10,
        lineHeight: "13px",
        fontWeight: 500,
        letterSpacing: "0.04em",
      },
    },
    onPillActive: {
      background: isDark ? "rgba(46,227,122,0.14)" : "rgba(37,211,102,0.12)",
      color: isDark ? "#7df0a8" : "#0e6c61",
      borderColor: isDark ? "rgba(46,227,122,0.28)" : "rgba(18,140,126,0.22)",
    },
    onPillInactive: {
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,22,20,0.04)",
      color: fgMuted,
      borderColor: hair,
    },

    // === Activity timeline ===
    timeline: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      borderTop: `1px solid ${hair}`,
    },
    actionRow: {
      display: "flex",
      gap: SP.sm,
      padding: "12px 12px",
      borderBottom: `1px solid ${hair}`,
      transition: "background 0.14s ease",
      "&:last-child": { borderBottom: "none" },
      "&:nth-child(even)": { background: accentTint },
      "&:hover": { background: accentSoft },
      // Squeeze guard: actionLabel + actionMeta wrapper (untyped Box) must shrink
      "& > *:not(:first-child)": {
        minWidth: 0,
        flex: "1 1 auto",
      },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense activity row: tight padding, no zebra striping
        padding: "8px 0",
        gap: 8,
        "&:nth-child(even)": { background: "transparent" },
        "&:hover": { background: "transparent" },
      },
    },
    actionTime: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: accent,
      flexShrink: 0,
      width: 64,
      fontFeatureSettings: '"tnum" 1',
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta token: 11/14 weight 400 muted, no accent on chrome
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: fgMuted,
        letterSpacing: "0em",
        textTransform: "none",
        width: 48,
      },
    },
    actionLabel: {
      fontSize: 13.5,
      fontWeight: 500,
      color: fg,
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense title token: 13/17 weight 500, wrap
        fontSize: 13,
        lineHeight: "17px",
        fontWeight: 500,
        color: fg,
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    actionMeta: {
      fontSize: 11.5,
      color: fgMuted,
      marginTop: 2,
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta token: 11/14 weight 400 muted
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: fgMuted,
      },
    },

    emptyState: {
      padding: "32px 12px",
      textAlign: "center",
      fontSize: 12.5,
      color: fgMuted,
      borderTop: `1px solid ${hair}`,
    },
    loadingWrap: {
      display: "grid",
      placeItems: "center",
      minHeight: 360,
    },
  };
});

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "?";

const formatRelativeTime = (iso) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "—";
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return i18n.t("admin.health.justNow", { defaultValue: "now" });
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
};

const formatDayShort = (yyyymmdd) => {
  try {
    const d = new Date(yyyymmdd);
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch {
    return yyyymmdd;
  }
};

const TenantHealthPanel = () => {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();
  const companyId = params.id;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(
        `/api/admin/companies/${companyId}/health`
      );
      setData(payload);
      setError(null);
    } catch (e) {
      setError(
        i18n.t("admin.health.loadError", {
          defaultValue: "Could not load tenant health.",
        })
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const sparkMax = useMemo(() => {
    if (!data?.msgVolumeLast7d?.length) return 1;
    return Math.max(1, ...data.msgVolumeLast7d.map((d) => d.count));
  }, [data]);

  const handleToggleAI = async () => {
    if (!data) return;
    const next = !data.configuration.aiEnabled;
    setActing("ai");
    try {
      const verb = next ? "enable" : "disable";
      await api.post(`/api/admin/companies/${companyId}/ai/${verb}`);
      toast.success(
        i18n.t(`admin.health.aiToggle.${verb}d`, {
          defaultValue: next ? "AI enabled" : "AI disabled",
        })
      );
      await load();
    } catch (e) {
      toast.error(
        i18n.t("admin.health.actionFailed", { defaultValue: "Action failed" })
      );
    }
    setActing(null);
  };

  const handleEnterTenant = () => {
    if (
      !window.confirm(
        `Enter ${data.company.name} as their admin? Your admin session is ` +
          `saved — use the banner at the top to return.`
      )
    )
      return;
    impersonateCompany(companyId).catch((e) =>
      toast.error(
        (e && e.response && e.response.data && e.response.data.error) ||
          (e && e.message) ||
          "Impersonation failed"
      )
    );
  };

  const handleSuspendToggle = async () => {
    if (!data) return;
    const isSuspended = data.company.status === "suspended";
    const verb = isSuspended ? "reactivate" : "suspend";
    if (
      !window.confirm(
        i18n.t(`admin.health.confirm.${verb}`, {
          name: data.company.name,
          defaultValue: isSuspended
            ? `Reactivate ${data.company.name}?`
            : `Suspend ${data.company.name}? They won't be able to log in.`,
        })
      )
    ) {
      return;
    }
    setActing("suspend");
    try {
      await api.post(`/api/admin/companies/${companyId}/${verb}`);
      toast.success(
        i18n.t(`admin.health.${verb}d`, {
          defaultValue: isSuspended ? "Reactivated" : "Suspended",
        })
      );
      await load();
    } catch (e) {
      toast.error(
        i18n.t("admin.health.actionFailed", { defaultValue: "Action failed" })
      );
    }
    setActing(null);
  };

  if (loading) {
    return (
      <Box className={classes.loadingWrap}>
        <CircularProgress size={28} style={{ color: "#128c7e" }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box className={classes.loadingWrap}>
        <Typography variant="body2" color="textSecondary">
          {error || "—"}
        </Typography>
      </Box>
    );
  }

  const { company, health, counts, msgVolumeLast7d, configuration, recentAdminActions } = data;

  const statusClass =
    health.status === "healthy" ? classes.statusHealthy :
    health.status === "watch" ? classes.statusWatch :
    classes.statusCritical;

  const StatusIcon =
    health.status === "healthy" ? CheckCircleRoundedIcon :
    health.status === "watch" ? WarningRoundedIcon :
    ErrorRoundedIcon;

  return (
    <Box className={classes.root}>
      {/* Breadcrumb back to Companies */}
      <Box
        className={classes.crumb}
        onClick={() => history.push("/admin/companies")}
        role="button"
        tabIndex={0}
      >
        <ArrowBackRoundedIcon className={classes.crumbIcon} />
        {i18n.t("admin.health.backToCompanies", {
          defaultValue: "Back to Companies",
        })}
      </Box>

      {/* Hero — tenant identity + status + accent meta */}
      <Box className={classes.hero}>
        <Box className={classes.heroLeft}>
          <span className={classes.heroAvatar}>{initials(company.name)}</span>
          <Box className={classes.heroStack}>
            <Typography className={classes.accentMeta} component="div">
              {i18n.t("admin.health.eyebrow", {
                defaultValue: "TENANT HEALTH",
              })}
            </Typography>
            <Typography className={classes.heroTitle} component="div">
              {company.name}
            </Typography>
            <Typography className={classes.heroSub} component="div">
              <BusinessRoundedIcon />
              ID #{company.id}
              {company.plan && (
                <>
                  <span className={classes.heroSubDot}>·</span>
                  {company.plan.name}
                </>
              )}
              <span className={classes.heroSubDot}>·</span>
              <span className={`${classes.statusPill} ${statusClass}`}>
                <StatusIcon />
                {i18n.t(`admin.health.status.${health.status}`, {
                  defaultValue: health.status,
                })}
              </span>
            </Typography>
            {health.reasons.length > 0 && (
              <Box style={{ marginTop: 8 }}>
                {health.reasons.map((r) => (
                  <span key={r} className={classes.reasonChip}>
                    {i18n.t(`admin.health.reason.${r}`, { defaultValue: r })}
                  </span>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Box className={classes.actions}>
          <button
            type="button"
            className={`${classes.btn}`}
            onClick={handleEnterTenant}
            title="Enter this tenant as their admin (audited, 30-min session)"
          >
            <ExitToAppRoundedIcon />
            {i18n.t("admin.health.actions.enterTenant", { defaultValue: "Enter tenant" })}
          </button>
          <button
            type="button"
            className={`${classes.btn}`}
            onClick={handleToggleAI}
            disabled={acting === "ai"}
            title={i18n.t("admin.health.toggleAiTip", {
              defaultValue: "Enable or disable AI worker for this tenant",
            })}
          >
            <SmartToyRoundedIcon />
            {configuration.aiEnabled
              ? i18n.t("admin.health.actions.disableAi", {
                  defaultValue: "Disable AI",
                })
              : i18n.t("admin.health.actions.enableAi", {
                  defaultValue: "Enable AI",
                })}
          </button>
          <button
            type="button"
            className={`${classes.btn} ${
              company.status === "suspended" ? classes.btnPrimary : classes.btnDanger
            }`}
            onClick={handleSuspendToggle}
            disabled={acting === "suspend"}
          >
            {company.status === "suspended"
              ? i18n.t("admin.health.actions.reactivate", {
                  defaultValue: "Reactivate",
                })
              : i18n.t("admin.health.actions.suspend", {
                  defaultValue: "Suspend",
                })}
          </button>
        </Box>
      </Box>

      {/* KPI grid — section head + 4 metric cards */}
      <Box>
        <Box className={classes.sectionHead}>
          <Box className={classes.sectionLabels}>
            <span className={classes.accentMeta}>
              {i18n.t("admin.health.kpi.eyebrow", { defaultValue: "AT A GLANCE" })}
            </span>
            <Typography className={classes.sectionTitle} component="div">
              {i18n.t("admin.health.kpi.title", { defaultValue: "Key metrics" })}
            </Typography>
          </Box>
        </Box>
      <Box className={classes.kpiGrid}>
        <Box className={classes.kpiCard}>
          <span className={classes.kpiLabel}>
            <ChatRoundedIcon />
            {i18n.t("admin.health.kpi.msgs24h", {
              defaultValue: "Messages (24h)",
            })}
          </span>
          <Typography className={classes.kpiValue}>{counts.msgsLast24h}</Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.health.kpi.totalLast24h", {
              defaultValue: "Inbound + outbound",
            })}
          </Typography>
        </Box>
        <Box className={classes.kpiCard}>
          <span className={classes.kpiLabel}>
            <ConfirmationNumberRoundedIcon />
            {i18n.t("admin.health.kpi.ticketsOpen", { defaultValue: "Open tickets" })}
          </span>
          <Typography className={classes.kpiValue}>{counts.ticketsOpen}</Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.health.kpi.closed24h", {
              count: counts.ticketsClosedLast24h,
              defaultValue: `${counts.ticketsClosedLast24h} closed in 24h`,
            })}
          </Typography>
        </Box>
        <Box className={classes.kpiCard}>
          <span className={classes.kpiLabel}>
            <WhatsAppIcon />
            {i18n.t("admin.health.kpi.whatsapps", { defaultValue: "WhatsApp" })}
          </span>
          <Typography className={classes.kpiValue}>
            {counts.whatsapps.connected}
            <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>
              /{counts.whatsapps.total}
            </span>
          </Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.health.kpi.connectedTotal", {
              defaultValue: "Connected / total",
            })}
          </Typography>
        </Box>
        <Box className={classes.kpiCard}>
          <span className={classes.kpiLabel}>
            <GroupRoundedIcon />
            {i18n.t("admin.health.kpi.users", { defaultValue: "Users" })}
          </span>
          <Typography className={classes.kpiValue}>{counts.users}</Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.health.kpi.queues", {
              count: counts.queues,
              defaultValue: `${counts.queues} queues`,
            })}
          </Typography>
        </Box>
      </Box>
      </Box>

      {/* Sparkline + Configuration */}
      <Box className={classes.twoCol}>
        <Box className={classes.panel}>
          <Box className={classes.sectionHead}>
            <Box className={classes.sectionLabels}>
              <span className={classes.accentMeta}>
                {i18n.t("admin.health.volume.eyebrow", {
                  defaultValue: "LAST 7 DAYS",
                })}
              </span>
              <Typography className={classes.sectionTitle} component="div">
                <ChatRoundedIcon />
                {i18n.t("admin.health.volume.title", {
                  defaultValue: "Message volume",
                })}
              </Typography>
            </Box>
            <Typography className={classes.sectionSub}>
              {i18n.t("admin.health.volume.sub", {
                defaultValue: "Daily totals · counts only",
              })}
            </Typography>
          </Box>
          <Box className={classes.sparkFrame}>
            <Box className={classes.sparkRow}>
              {msgVolumeLast7d.map((d) => {
                const heightPct = Math.max(4, Math.round((d.count / sparkMax) * 100));
                return (
                  <Box key={d.day} className={classes.sparkCol}>
                    <Typography className={classes.sparkValue}>{d.count}</Typography>
                    <Box className={classes.sparkBarWrap}>
                      <Box className={classes.sparkBar} style={{ height: `${heightPct}%` }} />
                    </Box>
                    <Typography className={classes.sparkLabel}>
                      {formatDayShort(d.day)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box className={classes.panel}>
          <Box className={classes.sectionHead}>
            <Box className={classes.sectionLabels}>
              <span className={classes.accentMeta}>
                {i18n.t("admin.health.config.eyebrow", {
                  defaultValue: "TENANT SETTINGS",
                })}
              </span>
              <Typography className={classes.sectionTitle} component="div">
                <SettingsRoundedIcon />
                {i18n.t("admin.health.config.title", {
                  defaultValue: "Configuration",
                })}
              </Typography>
            </Box>
            <Typography className={classes.sectionSub}>
              {i18n.t("admin.health.config.sub", {
                defaultValue: "Metadata only",
              })}
            </Typography>
          </Box>
          <Box className={classes.metricList}>
            <Box className={classes.configRow}>
              <Typography className={classes.configLabel}>
                {i18n.t("admin.health.config.ai", {
                  defaultValue: "AI worker",
                })}
              </Typography>
              <span
                className={`${classes.onPill} ${
                  configuration.aiEnabled
                    ? classes.onPillActive
                    : classes.onPillInactive
                }`}
              >
                {configuration.aiEnabled
                  ? i18n.t("admin.health.config.on", { defaultValue: "On" })
                  : i18n.t("admin.health.config.off", { defaultValue: "Off" })}
              </span>
            </Box>
            <Box className={classes.configRow}>
              <Typography className={classes.configLabel}>
                {i18n.t("admin.health.config.aiPromptSize", {
                  defaultValue: "AI prompt size",
                })}
              </Typography>
              <Typography className={classes.configValue}>
                {configuration.aiPromptLength}{" "}
                {i18n.t("admin.health.config.chars", { defaultValue: "chars" })}
              </Typography>
            </Box>
            <Box className={classes.configRow}>
              <Typography className={classes.configLabel}>
                <QueueRoundedIcon style={{ fontSize: 14, verticalAlign: "middle", marginInlineEnd: 6 }} />
                {i18n.t("admin.health.config.queues", { defaultValue: "Queues" })}
              </Typography>
              <Typography className={classes.configValue}>{counts.queues}</Typography>
            </Box>
            <Box className={classes.configRow}>
              <Typography className={classes.configLabel}>
                {i18n.t("admin.health.config.settings", {
                  defaultValue: "Settings rows",
                })}
              </Typography>
              <Typography className={classes.configValue}>
                {configuration.settingsCount}
              </Typography>
            </Box>
            {company.dueDate && (
              <Box className={classes.configRow}>
                <Typography className={classes.configLabel}>
                  {i18n.t("admin.health.config.dueDate", {
                    defaultValue: "Due date",
                  })}
                </Typography>
                <Typography className={classes.configValue}>
                  {company.dueDate}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Admin actions timeline */}
      <Box className={classes.panel}>
        <Box className={classes.sectionHead}>
          <Box className={classes.sectionLabels}>
            <span className={classes.accentMeta}>
              {i18n.t("admin.health.activity.eyebrow", {
                defaultValue: "AUDIT TRAIL",
              })}
            </span>
            <Typography className={classes.sectionTitle} component="div">
              <HistoryRoundedIcon />
              {i18n.t("admin.health.activity.title", {
                defaultValue: "Recent admin actions",
              })}
            </Typography>
          </Box>
          <Typography className={classes.sectionSub}>
            {i18n.t("admin.health.activity.sub", {
              defaultValue: "Platform operators only",
            })}
          </Typography>
        </Box>
        {recentAdminActions.length === 0 ? (
          <Box className={classes.emptyState}>
            {i18n.t("admin.health.activity.empty", {
              defaultValue: "No platform actions recorded for this tenant.",
            })}
          </Box>
        ) : (
          <Box className={classes.timeline}>
            {recentAdminActions.map((a, i) => (
              <Box key={`${a.at}-${i}`} className={classes.actionRow}>
                <Typography className={classes.actionTime}>
                  {formatRelativeTime(a.at)}
                </Typography>
                <Box>
                  <Typography className={classes.actionLabel}>
                    {i18n.t(`admin.health.action.${a.action}`, {
                      defaultValue: a.action,
                    })}
                  </Typography>
                  <Typography className={classes.actionMeta}>
                    {i18n.t("admin.health.activity.byUser", {
                      id: a.adminUserId,
                      defaultValue: `by admin #${a.adminUserId}`,
                    })}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TenantHealthPanel;
